import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import {
  LayoutDashboard, Users, GraduationCap, BookOpen, UserCog,
  LogOut, Menu, X, BookText, Wallet, FileSpreadsheet,
  ClipboardCheck, UserCheck, ListChecks,
  CalendarRange, FileText, Pencil, Trophy, ArrowUpCircle,
  CalendarDays, Briefcase, ScrollText, Settings, ShieldCheck, Activity,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "sekretaris", "ustadz"], end: true },
  { section: "Data", roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/santri", label: "Data Santri", icon: GraduationCap, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/kelas", label: "Data Kelas", icon: BookOpen, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/ustadz", label: "Data Ustadz", icon: Users, roles: ["admin", "sekretaris"] },
  { to: "/mata-pelajaran", label: "Mata Pelajaran", icon: BookText, roles: ["admin", "sekretaris"] },
  { to: "/jadwal", label: "Jadwal Pelajaran", icon: CalendarDays, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/assignments", label: "Penugasan Ustadz", icon: Briefcase, roles: ["admin"] },
  { section: "Absensi", roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/absensi/santri", label: "Absensi Santri", icon: ClipboardCheck, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/absensi/ustadz", label: "Absensi Ustadz", icon: UserCheck, roles: ["admin", "sekretaris"] },
  { to: "/absensi/rekap", label: "Rekap Absensi", icon: ListChecks, roles: ["admin", "sekretaris"] },
  { section: "Nilai Ujian", roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/jenis-ujian", label: "Jenis Ujian", icon: FileText, roles: ["admin", "sekretaris"] },
  { to: "/nilai/input", label: "Input Nilai", icon: Pencil, roles: ["admin", "ustadz"] },
  { to: "/nilai/progress", label: "Progress Penilaian", icon: Activity, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/nilai/rekap", label: "Rekap Nilai", icon: FileSpreadsheet, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/nilai/peringkat", label: "Peringkat Kelas", icon: Trophy, roles: ["admin", "sekretaris", "ustadz"] },
  { to: "/catatan-wali", label: "Catatan Wali Kelas", icon: MessageSquare, roles: ["admin", "sekretaris", "ustadz"] },
  { section: "Pembayaran", roles: ["admin", "sekretaris"] },
  { to: "/kitab", label: "Master Kitab", icon: BookText, roles: ["admin", "sekretaris"] },
  { to: "/pembayaran", label: "Pembayaran Kitab", icon: Wallet, roles: ["admin", "sekretaris"] },
  { to: "/laporan", label: "Laporan", icon: FileSpreadsheet, roles: ["admin", "sekretaris"] },
  { section: "Sistem", roles: ["admin", "sekretaris"] },
  { to: "/pengguna", label: "Manajemen Pengguna", icon: UserCog, roles: ["admin"] },
  { to: "/tahun-ajaran", label: "Tahun Ajaran", icon: CalendarRange, roles: ["admin"] },
  { to: "/kenaikan-kelas", label: "Kenaikan Kelas", icon: ArrowUpCircle, roles: ["admin"] },
  { to: "/import-export", label: "Import / Export", icon: FileSpreadsheet, roles: ["admin", "sekretaris"] },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["admin"] },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings, roles: ["admin"] },
  { to: "/backup", label: "Backup / Restore", icon: ShieldCheck, roles: ["admin"] },
];

function RoleBadge({ role }) {
  const map = {
    admin: "bg-emerald-400/20 text-emerald-300 border border-emerald-400/30",
    sekretaris: "bg-sky-400/20 text-sky-300 border border-sky-400/30",
    ustadz: "bg-amber-400/20 text-amber-300 border border-amber-400/30",
  };
  const label = { admin: "Admin", sekretaris: "Sekretaris", ustadz: "Ustadz" }[role] || role;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wider ${map[role] || "bg-slate-700 text-slate-200"}`}>
      {label}
    </span>
  );
}

function SidebarContent({ user, onNavigate }) {
  const items = navItems.filter((i) => i.roles.includes(user.role));
  return (
    <div className="flex flex-col h-full bg-[#123524] text-slate-100">
      <div className="px-5 py-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-xl bg-white p-1.5 shadow-md shrink-0">
            <img
              src="/assets/logo.png"
              alt="Logo Pondok Pesantren Sunan Giri"
              className="h-full w-full object-contain"
              data-testid="sidebar-logo"
            />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-white leading-tight text-xl">SiMadin</div>
            <div className="text-[10px] text-emerald-200/70 uppercase tracking-wider mt-0.5">
              Sunan Giri Surabaya
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-slate-300/70 leading-snug">
          Sistem Informasi Madrasah Diniyah
        </p>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {items.map((item, idx) => {
          if (item.section) {
            const isFirstSection = !items.slice(0, idx).some((i) => i.section);
            return (
              <div
                key={`s-${idx}`}
                className={`px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300/60 ${
                  isFirstSection ? "" : "mt-2 border-t border-white/10 pt-4"
                }`}
              >
                {item.section}
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-emerald-400/15 text-emerald-200 font-semibold shadow-[inset_2px_0_0_0_#34d399]"
                    : "text-slate-300 hover:bg-white/5 hover:text-white font-medium"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-emerald-300" : "text-slate-400"}`} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-white/10 bg-black/10">
        <div className="flex items-center gap-3 px-1">
          <div className="h-9 w-9 rounded-full bg-emerald-400/20 border border-emerald-400/30 flex items-center justify-center text-emerald-200 font-semibold text-sm">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-[11px] text-slate-300/60 truncate">{user.email}</div>
          </div>
        </div>
        <div className="mt-2 px-1"><RoleBadge role={user.role} /></div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-800">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <button
          data-testid="mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded-md hover:bg-slate-100"
          aria-label="Buka menu"
        >
          <Menu className="h-5 w-5 text-slate-700" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/assets/logo.png" alt="Logo" className="h-7 w-7 object-contain" />
          <span className="font-heading font-bold text-slate-900">SiMadin</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="user-menu-trigger" className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm">
            {user.name?.[0]?.toUpperCase() || "U"}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-button-mobile" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Keluar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 fixed inset-y-0 left-0 z-30 border-r border-slate-200">
          <SidebarContent user={user} />
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
            <aside className="relative w-72 max-w-[85%] h-full bg-white shadow-xl">
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-slate-100 z-10"
                aria-label="Tutup menu"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5 text-slate-700" />
              </button>
              <SidebarContent user={user} onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 lg:ml-64 min-h-screen flex flex-col">
          {/* Desktop topbar */}
          <header className="hidden lg:flex sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 h-14 items-center justify-end px-8">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="user-menu-trigger-desktop" className="gap-2">
                  <div className="h-7 w-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-xs">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="text-sm font-medium text-slate-800">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <div className="flex-1 p-4 md:p-8">
            <Outlet />
          </div>

          <Footer className="border-t border-slate-100 bg-white/70" />
        </main>
      </div>
    </div>
  );
}
